import React, { forwardRef, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '../Box';
import { RichTextEditorToolbar } from './_Toolbar/RichTextEditorToolbar';
import { buildChangeEvent } from '../../utils/reactEvents';
import { RichTextEditorFormat, TRichTextEditorFormat } from './models';
import { convertToQuillFormat, initRegisterPlugins, showMoreHrHandler } from './utils';
import { InputLabel } from '../InputLabel';
import { IDefaultInputProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';
import { getUniqueID } from '@src/utils/random';
import { AnyType } from '@src/models/common';
import { ENTER_KEY_PREVENTED } from './consts';

import styled from './RichTextEditor.module.scss';

const ReactQuill = lazy(() => import('react-quill'));

export interface IRichTextEditorProps extends IDefaultInputProps {
  formats?: TRichTextEditorFormat[];
  onKeyUp?: (e: AnyType) => void;
  isEnterKeyPrevented?: boolean;
  isShowToolbar?: boolean;
  preserveWhitespace?: boolean;
}

const defaultFormats = Object.values(RichTextEditorFormat);

/**
 * Wysiwyg редактор
 */
export const RichTextEditor = forwardRef<AnyType, IRichTextEditorProps>(
  (
    {
      id,
      onChange,
      disabled,
      value,
      label,
      formats = defaultFormats,
      placeholder,
      onKeyUp,
      isEnterKeyPrevented,
      isShowToolbar = true,
      preserveWhitespace,
    },
    ref,
  ) => {
    const [isPluginsInitialized, setIsPluginsInitialized] = useState<boolean>(false);

    useEffect(() => {
      // Важная тема!
      // Тут позволяем импортировать зависимости так, чтобы в SSR не упало
      initRegisterPlugins();
      // Только потом разрешаем подгружаться Quill-у,
      // иначе все кастомные html-и будут стерты, при этом
      // сам редактор не упадет - просто тихо подчистит все на onChange
      setIsPluginsInitialized(true);
    }, []);

    const handleOnChange = (value: string) => {
      if (onChange) {
        onChange(buildChangeEvent(value));
      }
    };

    const classes = classnames(styled.root, {});
    const toolbarId = useRef<string>(`toolbar_${getUniqueID()}`);

    const MODULES = useMemo(
      () => ({
        keyboard: isEnterKeyPrevented && ENTER_KEY_PREVENTED,
        toolbar: isShowToolbar && {
          container: `#${toolbarId.current}`,
          handlers: {
            hr: showMoreHrHandler,
          },
        },
      }),
      [isEnterKeyPrevented, isShowToolbar, toolbarId],
    );

    // Важно! Некоторые форматы встрены в либу react-quill и должны назваться как там https://www.npmjs.com/package/react-quill
    // Например header - это все хедеры

    const quillFormats = [] as string[];
    formats?.forEach((format) => {
      const quillFormat = convertToQuillFormat(format);
      if (quillFormat && !quillFormats.includes(quillFormat)) {
        quillFormats.push(quillFormat);
      }
    });

    return (
      <Box className={classes} column data-id={id} fullWidth>
        {toolbarId && (
          <>
            <InputLabel content={label} />
            <Box className={styled.editor} column>
              {isShowToolbar && (
                <Box>
                  <RichTextEditorToolbar id={toolbarId.current} formats={formats} />
                </Box>
              )}
              <Box fullWidth>
                {isPluginsInitialized && (
                  <Suspense>
                    <ReactQuill
                      ref={ref}
                      preserveWhitespace={preserveWhitespace}
                      placeholder={placeholder}
                      onKeyUp={onKeyUp}
                      value={value as string}
                      readOnly={disabled}
                      onChange={handleOnChange}
                      modules={MODULES}
                      formats={quillFormats}
                    />
                  </Suspense>
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>
    );
  },
);
